import type { MetricDef, MetricFormat, TargetsMap } from '@nono/contract';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { ApiError, api } from '@/lib/api';
import { metricTooltip } from '@/lib/format';
import { useLocale, useViewMessages } from '@/lib/i18n';
import { useMetrics } from '@/lib/queries';

const MESSAGES = {
  nb: {
    title: 'Mål',
    sub: 'Sett den sunne grensen per måltall. Styrer grønn/gul/rød fargelegging.',
    colMetric: 'Måltall',
    colDirection: 'Retning',
    colTarget: 'Mål',
    dirGte: '≥ minst',
    dirLte: '≤ høyst',
    save: 'Lagre',
    saving: 'Lagrer …',
    saved: 'Lagret',
    saveError: 'Feil',
    emptyForSegment: 'Kun Meta-baserte måltall kan begrenses i denne trakt-visningen.',
    globalNote: 'Mål er globale daglige grenser — delt på tvers av trakter (ikke per kampanje).',
    loadFailed: 'Kunne ikke laste måltall.',
    noBounded: 'Ingen begrensbare måltall for dette segmentet.',
    unitKr: 'kr',
    unitPct: '%',
    unitRatio: '×',
    higherBetter: 'Høyere er bedre',
    lowerBetter: 'Lavere er bedre',
    targetPlaceholder: 'Ikke satt',
  },
  en: {
    title: 'Targets',
    sub: 'Set the healthy bound per metric. Drives the green/amber/red coloring.',
    colMetric: 'Metric',
    colDirection: 'Direction',
    colTarget: 'Target',
    dirGte: '≥ at least',
    dirLte: '≤ at most',
    save: 'Save',
    saving: 'Saving …',
    saved: 'Saved',
    saveError: 'Error',
    emptyForSegment: 'Only Meta-side metrics can be bounded in this funnel view.',
    globalNote: 'Targets are global daily bounds — shared across funnels (not per-campaign).',
    loadFailed: 'Failed to load metrics.',
    noBounded: 'No bounded metrics for this segment.',
    unitKr: 'kr',
    unitPct: '%',
    unitRatio: '×',
    higherBetter: 'Higher is better',
    lowerBetter: 'Lower is better',
    targetPlaceholder: 'Not set',
  },
};

type ViewMessages = (typeof MESSAGES)['nb'];
type Comparator = 'gte' | 'lte';

/**
 * Human names for the dev-shorthand metric keys. The legacy view rendered
 * `label + " (" + format + ")"` (e.g. "CTR (pct)", "SUR-%", "R-ROI (ratio)"),
 * leaking internal enum tokens and cryptic abbreviations. We map every bounded
 * key to a plain-language name; the raw format token becomes a unit badge.
 */
const METRIC_NAME: Record<string, { nb: string; en: string }> = {
  cpm: { nb: 'CPM (kost per 1000 visninger)', en: 'CPM (cost per 1000 impressions)' },
  ctr: { nb: 'Klikkrate (CTR)', en: 'Click-through rate (CTR)' },
  cpc: { nb: 'Kost per klikk (CPC)', en: 'Cost per click (CPC)' },
  cpl: { nb: 'Kost per lead (CPL)', en: 'Cost per lead (CPL)' },
  lpPct: { nb: 'Landing → booking-rate', en: 'Landing → booking rate' },
  costPerBooking: { nb: 'Kost per booking', en: 'Cost per booking' },
  surPct: { nb: 'Oppmøterate', en: 'Show-up rate' },
  surCost: { nb: 'Kost per oppmøte', en: 'Cost per show-up' },
  ccPct: { nb: 'Sluttkonvertering', en: 'Close conversion' },
  lcPct: { nb: 'Lead → medlem-konvertering', en: 'Lead → member conversion' },
  revenue: { nb: 'Omsetning', en: 'Revenue' },
  cash: { nb: 'Kontant', en: 'Cash' },
  cpa: { nb: 'Kost per medlem (CAC)', en: 'Cost per member (CAC)' },
  rPl: { nb: 'Omsetnings-P/L', en: 'Revenue P/L' },
  cPl: { nb: 'Kontant-P/L', en: 'Cash P/L' },
  rRoi: { nb: 'Omsetnings-ROI', en: 'Revenue ROI' },
  cRoi: { nb: 'Kontant-ROI', en: 'Cash ROI' },
};

export function TargetsPage() {
  const m = useViewMessages(MESSAGES);
  const metricsQuery = useMetrics();

  if (metricsQuery.isLoading) {
    return (
      <PageFrame m={m}>
        <LoadingState />
      </PageFrame>
    );
  }
  if (metricsQuery.isError || !metricsQuery.data) {
    return (
      <PageFrame m={m}>
        <ErrorState onRetry={() => metricsQuery.refetch()} />
      </PageFrame>
    );
  }

  const data = metricsQuery.data;
  const rows = data.defs.filter(
    (d) => ['nok', 'pct', 'ratio'].includes(d.format) && d.higherIsBetter !== null,
  );

  return (
    <PageFrame m={m}>
      {/* Honest, centralized caveat: targets are global daily bounds, not per-funnel. */}
      <p className="flex items-start gap-2 rounded-md border border-border bg-surface px-3 py-2 text-label text-fg-muted">
        <CaveatRef id="colorLegend" />
        <span>
          {data.segment !== 'all' ? `${m.globalNote} ${m.emptyForSegment}` : m.globalNote}
        </span>
      </p>

      {rows.length === 0 ? (
        <EmptyState title={m.noBounded}>{m.emptyForSegment}</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-collapse text-body">
            <thead>
              <tr className="border-b border-border bg-surface-2">
                <th className="sticky left-0 z-10 bg-surface-2 px-3 py-2 text-left text-label font-medium text-fg-muted">
                  {m.colMetric}
                </th>
                <th className="px-3 py-2 text-left text-label font-medium text-fg-muted">
                  <span className="inline-flex items-center gap-1">
                    {m.colDirection}
                    {/* This comparator drives the green/amber/red coloring on Overview & Trends. */}
                    <CaveatRef id="colorLegend" />
                  </span>
                </th>
                <th className="px-3 py-2 text-left text-label font-medium text-fg-muted">
                  {m.colTarget}
                </th>
                <th className="px-3 py-2 text-right text-label font-medium text-fg-muted" />
              </tr>
            </thead>
            <tbody>
              {rows.map((def) => (
                <TargetRow key={def.key} def={def} targets={data.targets} m={m} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageFrame>
  );
}

function PageFrame({ m, children }: { m: ViewMessages; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
      </header>
      {children}
    </div>
  );
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

function TargetRow({ def, targets, m }: { def: MetricDef; targets: TargetsMap; m: ViewMessages }) {
  const { locale } = useLocale();
  const queryClient = useQueryClient();

  const current = targets[def.key];
  const defaultComparator: Comparator = def.higherIsBetter ? 'gte' : 'lte';

  const [comparator, setComparator] = useState<Comparator>(
    current?.comparator === 'lte' || current?.comparator === 'gte'
      ? current.comparator
      : defaultComparator,
  );
  // Empty string when no target set — never seed a 0 (parity edge case).
  const [value, setValue] = useState<string>(current ? String(current.targetValue) : '');
  const [status, setStatus] = useState<SaveStatus>('idle');

  const mutation = useMutation({
    mutationFn: (parsed: number) => {
      // notes is round-tripped, never editable — preserve any seeded note.
      const existingNotes = current?.notes ?? null;
      return api.putTarget({
        metricKey: def.key,
        comparator,
        targetValue: parsed,
        notes: existingNotes,
      });
    },
    onSuccess: () => {
      // Re-read the whole map so sibling rows reflect server truth (legacy
      // replaced m.targets with res.targets); invalidate both caches that
      // carry the targets map.
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
      void queryClient.invalidateQueries({ queryKey: ['metrics'] });
      setStatus('saved');
      window.setTimeout(() => setStatus('idle'), 1200);
    },
    onError: () => {
      setStatus('error');
    },
  });

  const parsed = Number.parseFloat(value);
  const canSave = value.trim() !== '' && Number.isFinite(parsed);

  const onSave = () => {
    if (!canSave) return;
    setStatus('saving');
    mutation.mutate(parsed);
  };

  const name = METRIC_NAME[def.key]?.[locale] ?? def.label;
  const unit = unitFor(def.format, m);
  const polarityLabel = def.higherIsBetter ? m.higherBetter : m.lowerBetter;

  return (
    <tr className="border-b border-border/60 hover:bg-surface-2/50">
      {/* Metric identity (sticky-left) + polarity glyph + desc tooltip. */}
      <th className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-normal sm:whitespace-nowrap">
        <span className="inline-flex items-center gap-2">
          {def.higherIsBetter ? (
            <ArrowUp className="size-3.5 shrink-0 text-good-fg" aria-label={polarityLabel} />
          ) : (
            <ArrowDown className="size-3.5 shrink-0 text-good-fg" aria-label={polarityLabel} />
          )}
          <span
            className="max-w-[160px] truncate text-fg sm:max-w-none"
            title={metricTooltip(def.desc, def.source)}
          >
            {name}
          </span>
          {unit ? (
            <span className="rounded-sm bg-surface-3 px-1.5 py-0.5 text-micro font-medium text-fg-muted">
              {unit}
            </span>
          ) : null}
        </span>
      </th>

      {/* Direction (comparator) */}
      <td className="px-3 py-2">
        <select
          aria-label={`${m.colDirection} — ${name}`}
          value={comparator}
          onChange={(e) => setComparator(e.target.value === 'lte' ? 'lte' : 'gte')}
          className="h-10 rounded-md border border-border bg-surface px-2 text-label text-fg transition-fast hover:bg-surface-2 focus:border-border-strong sm:h-8"
        >
          <option value="gte">{m.dirGte}</option>
          <option value="lte">{m.dirLte}</option>
        </select>
      </td>

      {/* Target value (numeric) with unit affix */}
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          <input
            type="number"
            step="any"
            inputMode="decimal"
            aria-label={`${m.colTarget} — ${name}`}
            placeholder={m.targetPlaceholder}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (status !== 'idle') setStatus('idle');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSave();
            }}
            className="h-10 w-28 rounded-md border border-border bg-surface px-2 text-right text-label tabular text-fg transition-fast focus:border-border-strong sm:h-8"
          />
          {unit ? <span className="text-label text-fg-subtle">{unit}</span> : null}
        </span>
      </td>

      {/* Per-row Save + inline status */}
      <td className="px-3 py-2 text-right">
        <span className="inline-flex items-center justify-end gap-2">
          {status === 'saved' ? <span className="text-label text-good-fg">{m.saved}</span> : null}
          {status === 'error' ? (
            <span className="text-label text-bad-fg" title={errorDetail(mutation.error)}>
              {m.saveError}
            </span>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={!canSave || status === 'saving'}
          >
            {status === 'saving' ? m.saving : m.save}
          </Button>
        </span>
      </td>
    </tr>
  );
}

/** Humanized unit affix for the bounded formats. int/text never reach here. */
function unitFor(format: MetricFormat, m: ViewMessages): string {
  switch (format) {
    case 'nok':
      return m.unitKr;
    case 'pct':
      return m.unitPct;
    case 'ratio':
      return m.unitRatio;
    default:
      return '';
  }
}

/** Surface the backend message instead of swallowing it (legacy showed bare "Error"). */
function errorDetail(error: unknown): string | undefined {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return undefined;
}
